import { AnimatePresence, motion } from 'motion/react'
import { ReactNode, useMemo, useState, useEffect } from 'react'
import { LoadingAnimation } from '@/components'
import useHasRendered from '@/hooks/useHasRendered'

type FadeTransitionProps = {
  children: ReactNode
  modeKey: string | number
  duration?: number
  className?: string
}

export const FadeTransition = ({
  children,
  modeKey,
  duration = 0.3,
  className,
}: FadeTransitionProps) => {
  const hasRenderedOnce = useHasRendered()

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={modeKey}
        initial={{ opacity: hasRenderedOnce ? 0 : 1 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration, ease: 'easeInOut' }}
        className={className}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  )
}

type LoadingContainerProps = {
  isLoading: boolean
  loadedContent?: React.ReactNode
  loadingContent?: React.ReactNode
  children?: React.ReactNode
  loadingDelay?: number
  className?: string
}

export const LoadingContainer = ({
  isLoading,
  loadedContent,
  loadingContent,
  children,
  loadingDelay = 200,
  className,
}: LoadingContainerProps) => {
  const hasRenderedOnce = useHasRendered()
  const [showLoading, setShowLoading] = useState(!hasRenderedOnce)

  useEffect(() => {
    let timeoutId: NodeJS.Timeout

    if (isLoading) {
      if (!hasRenderedOnce) {
        setShowLoading(true)
      } else {
        // On subsequent renders, use the delay
        timeoutId = setTimeout(() => {
          setShowLoading(true)
        }, loadingDelay)
      }
    } else {
      // Immediately hide loading when isLoading becomes false
      setShowLoading(false)
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [isLoading, loadingDelay, hasRenderedOnce])

  const defaultLoading = useMemo(
    () => <LoadingAnimation type="falling" centered />,
    [],
  )

  const usedLoadingContent = loadingContent || defaultLoading
  const usedLoadedContent = loadedContent || children

  return (
    <FadeTransition
      className={className}
      duration={0.2}
      modeKey={showLoading ? 'loading' : 'loaded'}
    >
      {showLoading ? usedLoadingContent : usedLoadedContent}
    </FadeTransition>
  )
}
